import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import LuggageIcon from "@mui/icons-material/Luggage";
import FiberNewIcon from "@mui/icons-material/FiberNew";

function showBookedProduct(slot, isUnassigned, person) {
  return slot === "AFTERNOON" && isUnassigned && !!person.bookedProductName;
}

function showUnassignedWarning(slot, isUnassigned, person) {
  if (!isUnassigned) return false;
  if (person.personType === "ADULT") return false;

  // Only meaningful for afternoon where booked products exist
  if (slot === "AFTERNOON") {
    return !person.bookedProductName;
  }

  return false;
}

function buildLabel(person, isUnassigned, slot) {
  const suffix = person.personType === "ADULT" ? " (Adult)" : "";

  if (showBookedProduct(slot, isUnassigned, person)) {
    return `${person.name || "Unknown"}${suffix} - ${person.bookedProductName}`;
  }

  return `${person.name || "Unknown"}${suffix}`;
}

function getActivityCardStyles(isUnassigned, colorIndex, isAlert) {
  if (isUnassigned) {
    // Only alarm (red) when students are unassigned; adults-only is neutral, not a problem.
    return isAlert
      ? { backgroundColor: "#fdeaea", border: "1px solid #e6b3b3" }
      : { backgroundColor: "#f5f5f5", border: "1px solid #e0e0e0" };
  }

  const palette = [
    { backgroundColor: "#eaf4ea", border: "1px solid #b9d7b9" },
    { backgroundColor: "#eaf2fd", border: "1px solid #b8cdee" },
    { backgroundColor: "#fff8e6", border: "1px solid #ead79c" },
    { backgroundColor: "#f3ecfb", border: "1px solid #ccb7e8" },
    { backgroundColor: "#eaf7f6", border: "1px solid #b7dcd8" },
  ];

  return palette[colorIndex % palette.length];
}

function sortResidents(residents = []) {
  return [...residents].sort((a, b) => {
    if (a.personType === "ADULT" && b.personType !== "ADULT") return -1;
    if (a.personType !== "ADULT" && b.personType === "ADULT") return 1;
    return (a.name || "").localeCompare(b.name || "");
  });
}

export default function ActivityCard({
  activityName,
  residents = [],
  leaderName,
  isUnassigned = false,
  colorIndex = 0,
  slot = "AFTERNOON",
  tv = false,
  tvColumns = 3,
}) {
  const [openState, setOpen] = useState(false);
  const open = tv || openState; // TV view: always expanded

  const sortedResidents = useMemo(
    () => sortResidents(residents),
    [residents]
  );

  // Unassigned is only an alarm (red) when actual students are unassigned.
  const isAlert =
    isUnassigned && residents.some((person) => person.personType === "STUDENT");

  return (
    <Card
      sx={{
        width: tv ? tvColumns * 250 : 320,
        ...getActivityCardStyles(isUnassigned, colorIndex, isAlert),
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.5}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isAlert ? "error.main" : "inherit",
              }}
            >
              {activityName || "Unnamed activity"}
            </Typography>

            {!isUnassigned && leaderName ? (
              <Typography variant="body2" color="text.secondary">
                Leader: {leaderName}
              </Typography>
            ) : null}
          </Stack>

          {tv ? null : (
            <IconButton onClick={() => setOpen((prev) => !prev)}>
              <ExpandMoreIcon
                sx={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </IconButton>
          )}
        </Stack>

        <Typography variant="body2" sx={{ mt: 1 }}>
          {residents.length}{" "}
          {isUnassigned
            ? residents.length === 1
              ? "unassigned person"
              : "unassigned people"
            : residents.length === 1
            ? "member"
            : "members"}
        </Typography>

        <Collapse in={open}>
          <Box sx={{ mt: 2, columnCount: tv ? tvColumns : 1, columnGap: "24px" }}>
            {sortedResidents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No one assigned
              </Typography>
            ) : (
              sortedResidents.map((person) => {
                const isAdult = person.personType === "ADULT";
                const arrivingToday =
                  person.arrivingToday === 1 || person.arrivingToday === true;
                const departingToday =
                  person.departingToday === 1 || person.departingToday === true;

                return (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    key={`${person.personType}-${person.personId}`}
                    sx={{ breakInside: "avoid", mb: 1 }}
                  >
                    {arrivingToday ? (
                      <FiberNewIcon color="primary" fontSize="small" />
                    ) : null}

                    {departingToday ? (
                      <LuggageIcon color="action" fontSize="small" />
                    ) : null}

                    {showUnassignedWarning(slot, isUnassigned, person) ? (
                      <WarningIcon color="warning" fontSize="small" />
                    ) : null}

                    <Chip
                      label={buildLabel(person, isUnassigned, slot)}
                      size="small"
                      sx={{
                        fontWeight: isAdult ? 600 : 400,
                      }}
                    />
                  </Stack>
                );
              })
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}